import { FormsModule } from "@angular/forms";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input,
    OnChanges,
    OnInit,
    SimpleChanges,
    ViewEncapsulation,
} from "@angular/core";
import { S25DropdownPaginatedModule } from "../../s25-dropdown/s25.dropdown.paginated.module";
import { IngestionPointCategory } from "../data.ingestion.const";
import { OrganizationService } from "../../../services/organization.service";
import { DropDownItem } from "../../../pojo/DropDownItem";
import { Item } from "../../../pojo/Item";
import { S25Util } from "../../../util/s25-util";

@TypeManagerDecorator("s25-ng-ingestion-subcategory-selector")
@Component({
    selector: "s25-ng-ingestion-subcategory-selector",
    template: `
        @if (category === "ENGAGEMENT") {
            @if (isEditing) {
                <s25-organization-dropdown
                    [(value)]="selectedOrganization"
                    [hasNoneItem]="allowClear"
                    [noneItemLabel]="'No organization'"
                    (valueChange)="onOrganizationChange($event)"
                />
            } @else {
                <div class="organization-display">
                    @if (organizationName) {
                        <span class="organization-name">{{ organizationName }}</span>
                    } @else {
                        <span class="no-organization">No organization selected</span>
                    }
                </div>
            }
        } @else {
            <input
                class="c-input"
                type="text"
                [(ngModel)]="subCategory"
                (ngModelChange)="onSubCategoryTextChange($event)"
                [placeholder]="'Enter sub-category'"
                [disabled]="!isEditing"
            />
        }
    `,
    styles: `
        .organization-display,
        .standalone-display {
            padding: 0.5rem;
            background-color: var(--color--surface-2-bg);
            border: 1px solid var(--color--border);
            border-radius: 4px;
            min-height: 38px;
            display: flex;
            align-items: center;
        }

        .organization-name {
            font-weight: 500;
            color: var(--color--text);
        }

        .no-organization {
            color: var(--color--text-muted);
            font-style: italic;
        }

        .standalone-text {
            color: #6c757d;
            font-style: italic;
        }

        s25-organization-dropdown {
            display: inline-block;
            max-width: 350px;
            width: 80%;
        }
    `,
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [S25DropdownPaginatedModule, FormsModule],
})
export class IngestionSubcategorySelectorComponent implements OnInit, OnChanges {
    @Input() category: IngestionPointCategory;
    @Input() subCategory: string;
    @Input() isEditing: boolean = true;
    @Input() allowClear: boolean = true;
    @Input() onSubCategoryChange: (subCategory: string) => void;

    selectedOrganization: DropDownItem | undefined;
    organizationName: string | undefined;

    private subCategorySyncToken = 0;

    constructor(public cd: ChangeDetectorRef) {}

    ngOnInit() {
        void this.syncOrganizationFromSubCategory();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.subCategory || changes.category) {
            void this.syncOrganizationFromSubCategory();
        }
    }

    onOrganizationChange = (organization: DropDownItem | null | undefined) => {
        if (organization?.itemId) {
            this.onSubCategoryChange(organization.itemId.toString());
        } else {
            this.onSubCategoryChange("");
        }
    };

    onSubCategoryTextChange = (value: string) => {
        this.onSubCategoryChange(value);
    };

    private async syncOrganizationFromSubCategory(): Promise<void> {
        const syncToken = ++this.subCategorySyncToken;

        if (this.category !== "ENGAGEMENT" || !this.subCategory?.trim()) {
            this.selectedOrganization = undefined;
            this.organizationName = undefined;
            this.cd.markForCheck();
            return;
        }

        const orgId = S25Util.parseInt(this.subCategory);
        if (!orgId) {
            this.selectedOrganization = undefined;
            this.organizationName = undefined;
            this.cd.markForCheck();
            return;
        }

        try {
            const orgData = await OrganizationService.getOrganizationNameAndFormal(orgId);
            if (syncToken !== this.subCategorySyncToken) {
                return;
            }

            const orgName = orgData.itemName ?? "";
            this.organizationName = orgName;
            this.selectedOrganization = {
                itemId: orgId,
                val: orgId,
                itemName: orgName,
                txt: orgName,
                dropDownOrigName: orgName,
                itemTypeId: Item.Ids.Organization,
            };
        } catch (error) {
            if (syncToken !== this.subCategorySyncToken) {
                return;
            }

            console.error("Failed to load organization name:", error);
            this.organizationName = "Unknown Organization";
            this.selectedOrganization = undefined;
        }

        this.cd.markForCheck();
    }
}
